// PointsGestion.js - VERSION ADAPTÉE POUR PRODUCT V (VALISES)
import React, { useState, useEffect } from "react";
import axios from "axios";
import './PointsGestion.css';

function PointsGestion() {
    const [subProducts, setSubProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("Tous");
    const [searchTerm, setSearchTerm] = useState("");
    const [bulkPoints, setBulkPoints] = useState("");

    // Charger tous les sous-produits de type Valises/V
    const loadSubProducts = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");

            // Charger tous les produits avec leurs sous-produits (API ProductV)
            const response = await axios.get("/api/ProductV/getProductV", {
                headers: { Authorised: token }
            });

            console.log("Données reçues de l'API ProductV:", response.data);

            // Extraire et aplatir tous les sous-produits (subProductsV)
            const allSubsRaw = response.data.flatMap(product =>
                (product.subProductsV || []).map(sub => ({
                    ...sub,
                    parentProductId: product._id,
                    parentProductName: product.name,
                    parentProductCategory: product.category,
                    giftPoints: sub.giftPoints || 0
                }))
            );

            // Éliminer les doublons réels par ID (si un sous-produit apparaît dans plusieurs produits parents)
            const seenIds = new Set();
            const allSubs = allSubsRaw.filter(sub => {
                if (seenIds.has(sub._id)) return false;
                seenIds.add(sub._id);
                return true;
            });

            console.log("Sous-produits extraits (sans doublons):", allSubs.length);
            setSubProducts(allSubs);

        } catch (error) {
            console.error("Erreur chargement:", error);
            alert("Erreur lors du chargement des produits");
        } finally {
            setLoading(false);
        }
    };

    // Initialisation
    useEffect(() => {
        loadSubProducts();
    }, []);

    // Mettre à jour les points d'un sous-produit (API ProductV)
    const updatePoints = async (subProductId, newPoints) => {
        try {
            const token = localStorage.getItem("token");

            await axios.put(
                `/api/ProductV/subproductV/${subProductId}/points`,
                { giftPoints: parseInt(newPoints) || 0 },
                { headers: { Authorised: token } }
            );

            // Mettre à jour localement
            setSubProducts(prev => prev.map(sp =>
                sp._id === subProductId
                    ? { ...sp, giftPoints: parseInt(newPoints) || 0 }
                    : sp
            ));

            return true;
        } catch (error) {
            console.error("Erreur mise à jour:", error);
            alert("❌ Erreur lors de la mise à jour");
            return false;
        }
    };

    // Mettre à jour plusieurs sous-produits (API ProductV)
    const updateMultiplePoints = async (pointsValue) => {
        if (!pointsValue || isNaN(pointsValue)) {
            alert("Veuillez entrer une valeur valide");
            return;
        }

        const points = parseInt(pointsValue);
        const filtered = getFilteredSubProducts();

        if (filtered.length === 0) return;

        if (!window.confirm(`Définir ${points} points pour ${filtered.length} produit(s) ?`)) return;

        try {
            const token = localStorage.getItem("token");
            const updates = filtered.map(sp => ({
                subProductId: sp._id,
                giftPoints: points
            }));

            await axios.post('/api/ProductV/bulk-update-points',
                { updates },
                { headers: { Authorised: token } }
            );

            // Mettre à jour localement
            setSubProducts(prev => prev.map(sp => {
                if (filtered.some(fsp => fsp._id === sp._id)) {
                    return { ...sp, giftPoints: points };
                }
                return sp;
            }));

            alert(`✅ ${filtered.length} produit(s) mis à jour`);
            setBulkPoints("");

        } catch (error) {
            console.error("Erreur:", error);
            alert("❌ Erreur mise à jour multiple");
        }
    };

    // Filtrer les sous-produits
    const getFilteredSubProducts = () => {
        let filtered = [...subProducts];

        // Filtre par catégorie
        if (selectedCategory !== "Tous") {
            filtered = filtered.filter(sp => sp.parentProductCategory === selectedCategory);
        }

        // Filtre par recherche
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(sp =>
                sp.name.toLowerCase().includes(term) ||
                sp.parentProductName.toLowerCase().includes(term) ||
                (sp.barcode && sp.barcode.toLowerCase().includes(term))
            );
        }

        return filtered;
    };

    const filteredSubProducts = getFilteredSubProducts();

    // Obtenir les catégories uniques
    const categories = ["Tous", ...new Set(subProducts.map(sp => sp.parentProductCategory).filter(Boolean))];

    // Points prédéfinis
    const predefinedPoints = [0, 1, 2, 3, 5, 10, 15, 20];

    // Calculer les statistiques
    const stats = {
        total: subProducts.length,
        withPoints: subProducts.filter(sp => sp.giftPoints > 0).length,
        totalPoints: subProducts.reduce((sum, sp) => sum + (sp.giftPoints || 0), 0)
    };

    return (
        <div className="points-gestion">
            {/* En-tête */}
            <div className="header">
                <h1>🎁 Points Kdo - Valises & Jouets</h1>
                <div className="stats">
                    <div className="stat">
                        <span className="value">{stats.total}</span>
                        <span className="label">Articles</span>
                    </div>
                    <div className="stat">
                        <span className="value">{stats.withPoints}</span>
                        <span className="label">Avec points</span>
                    </div>
                    <div className="stat">
                        <span className="value">{stats.totalPoints}</span>
                        <span className="label">Points totaux</span>
                    </div>
                </div>
            </div>

            {/* Filtres rapides */}
            <div className="filters">
                <div className="filter-group">
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>

                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Nom, Parent ou Code-barres..."
                    />

                    <div className="bulk-update">
                        <input
                            type="number"
                            value={bulkPoints}
                            onChange={(e) => setBulkPoints(e.target.value)}
                            placeholder="Points"
                            min="0"
                            max="50"
                        />
                        <button
                            onClick={() => updateMultiplePoints(bulkPoints)}
                            disabled={!bulkPoints}
                        >
                            Appliquer à {filteredSubProducts.length}
                        </button>
                    </div>

                    <button onClick={loadSubProducts} className="refresh">
                        🔄 Actualiser
                    </button>
                </div>
            </div>

            {/* Liste des sous-produits */}
        // Remplacez la section "Liste des sous-produits" dans votre JSX par :

            <div className="products-list">
                {loading ? (
                    <div className="loading">Chargement des données...</div>
                ) : filteredSubProducts.length > 0 ? (
                    <div className="products-table-container">
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>PRODUIT</th>
                                    <th>CATÉGORIE</th>
                                    <th>PRIX</th>
                                    <th>POINTS</th>
                                    {predefinedPoints.map(points => (
                                        <th key={points}>{points}</th>
                                    ))}
                                    <th>PERSO</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSubProducts.map((subProduct, index) => (
                                    <tr key={subProduct._id}>
                                        <td style={{ textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                                            {index + 1}
                                        </td>
                                        <td>
                                            <div className="product-info-cell">
                                                <div className="product-name">{subProduct.name}</div>
                                                <div className="product-meta">
                                                    <span className="product-category">{subProduct.parentProductCategory}</span>
                                                    <span className="product-parent">{subProduct.parentProductName}</span>
                                                    {subProduct.barcode && (
                                                        <span className="product-barcode">📦 {subProduct.barcode}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="product-category">{subProduct.parentProductCategory}</span>
                                        </td>
                                        <td>
                                            <div className="product-price">
                                                {Number(subProduct.price || 0).toFixed(2)} DT
                                            </div>
                                        </td>
                                        <td className="current-points-cell">
                                            <span className="current-points-value">
                                                {subProduct.giftPoints || 0}
                                            </span>
                                        </td>
                                        {predefinedPoints.map(points => (
                                            <td key={points} className="points-buttons-cell">
                                                <button
                                                    className={`points-button ${subProduct.giftPoints === points ? 'active' : ''}`}
                                                    onClick={() => updatePoints(subProduct._id, points)}
                                                    title={`Définir à ${points} points`}
                                                >
                                                    {points}
                                                </button>
                                            </td>
                                        ))}
                                        <td className="custom-input-cell">
                                            <div className="custom-input-container">
                                                <input
                                                    type="number"
                                                    value={subProduct.giftPoints || ""}
                                                    onChange={(e) => {
                                                        const value = Math.max(0, parseInt(e.target.value) || 0);
                                                        updatePoints(subProduct._id, value);
                                                    }}
                                                    min="0"
                                                    max="50"
                                                    placeholder="0"
                                                />
                                                <span>pts</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="empty">
                        📦 Aucun article trouvé
                        <button onClick={loadSubProducts}>Réessayer</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PointsGestion;